import { ComponentPropsWithoutRef, useContext, useMemo } from 'react';
import { flexRender } from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { CSS, useDraggable, useDroppable } from '@givebutter/primitives/draggable';
import {
    faBarsFilter,
    faChartLineUp,
    faCheck,
    faEyeSlash,
    faSnowflake,
    faSnowflakeDroplets,
} from '@fortawesome/pro-regular-svg-icons';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import * as DropdownMenu from 'components/dropdown-menu';
import { defaultFilterGroups } from 'components/filter/filters-next/defaults';
import { TableConstants, TableIgnoredColumns } from 'components/table/TableConstants';
import { TableContext, TableHeaderContext } from 'components/table/TableContext';
import { TableHeaderData } from 'components/table/TableHeaderData';
import { TableHeaderSort } from 'components/table/TableHeaderSort';
import { useIsStickyMediaQuery } from 'components/table/useIsStickyMediaQuery';
import {
    ascIconFactory,
    descIconFactory,
    getComputedColumnProps,
    isStickyColumn,
} from 'components/table/util';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { twJoin, twMerge } from 'tailwind-merge';

interface TableHeaderDataActionableProps
    extends Omit<ComponentPropsWithoutRef<'th'>, 'children' | 'className'> {}

const TableHeaderDataActionable = (props: TableHeaderDataActionableProps) => {
    const isMobile = useIsStickyMediaQuery();
    const { table, variant } = useContext(TableContext);
    const { header } = useContext(TableHeaderContext);
    const { style: styleConsumer, ...rest } = props;
    const canSort = header.column.getCanSort();
    const canOrder = table.options.meta?.enableColumnOrder ?? false;
    const canFreeze = header.column.getCanPin();
    const canHide = header.column.getCanHide();
    const canVisualize = header.column.columnDef.meta?.visualize ?? false;
    const canFilter = header.column.columnDef.meta?.columnFilterable ?? false;
    const isActionColumn = TableIgnoredColumns.includes(header.column.id);
    const canInteract =
        (canSort || canOrder || canFreeze || canHide || canVisualize || canFilter) &&
        !isActionColumn;
    const columnSortType = header.column.columnDef.meta?.sortType ?? 'alpha';

    const { isDragging, attributes, listeners, setNodeRef, transform } = useDraggable({
        id: header.id,
        disabled: isActionColumn || !canOrder,
    });

    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
        id: header.id,
        disabled: isActionColumn || !canOrder,
    });

    const className = header.column.columnDef.meta?.headerClassName ?? '';
    const variantClassName = variant === 'bordered' && 'bg-gray-50';
    const draggingClassName = isDragging && 'z-pentOutermost bg-gray-200';
    const droppableClassName = canInteract && canOrder && !isDragging && isOver && 'bg-blue-100';
    const { pinnedClassName } = getComputedColumnProps(table, header.column);

    const style = {
        ...styleConsumer,
        transform: CSS.Translate.toString(transform),
        left:
            !isMobile && isStickyColumn(header.column)
                ? `var(${TableConstants.ColumnOffsetPropertyPrefix}-${header.column.id})`
                : undefined,
    };

    const children = useMemo(
        () =>
            header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext()),
        [header],
    );

    return (
        <TableHeaderData
            ref={ref => {
                setNodeRef(ref);
                setDroppableNodeRef(ref);
            }}
            data-table-header-id={header.column.id}
            className={twMerge(
                variantClassName,
                draggingClassName,
                droppableClassName,
                pinnedClassName,
                className,
                'group/th',
            )}
            style={style}
            aria-label={'Table column header'}
            {...rest}
        >
            {isActionColumn ? (
                children
            ) : (
                <div className={'flex justify-between gap-4'}>
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger
                            disabled={!canInteract}
                            className={twJoin(
                                canInteract &&
                                    '-mx-1.5 -my-1 w-fit cursor-pointer rounded-md px-1.5 py-1 hover:bg-gray-200',
                                'flex flex-nowrap items-center gap-2',
                            )}
                        >
                            {children}
                            {header.column.getCanSort() && <TableHeaderSort />}
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content sideOffset={6} align={'start'}>
                            {canSort && (
                                <DropdownMenu.Item
                                    onClick={() => {
                                        header.column.toggleSorting(false);
                                        table.options.meta?.onSortColumn?.(header.column, false);
                                    }}
                                    className="flex justify-between gap-4"
                                >
                                    <div className={'flex items-center gap-1.5'}>
                                        <SquaredIcon icon={ascIconFactory(columnSortType)} />
                                        <p>Sort ascending</p>
                                    </div>
                                    {header.column.getIsSorted() === 'asc' && (
                                        <FontAwesomeIcon
                                            size={'sm'}
                                            className={'text-blue-500'}
                                            icon={faCheck}
                                        />
                                    )}
                                </DropdownMenu.Item>
                            )}
                            {canSort && (
                                <DropdownMenu.Item
                                    onClick={() => {
                                        header.column.toggleSorting(true);
                                        table.options.meta?.onSortColumn?.(header.column, true);
                                    }}
                                    className="flex justify-between gap-4"
                                >
                                    <div className={'flex items-center gap-1.5'}>
                                        <SquaredIcon icon={descIconFactory(columnSortType)} />
                                        <p>Sort descending</p>
                                    </div>
                                    {header.column.getIsSorted() === 'desc' && (
                                        <FontAwesomeIcon
                                            size={'sm'}
                                            className={'text-blue-500'}
                                            icon={faCheck}
                                        />
                                    )}
                                </DropdownMenu.Item>
                            )}
                            {canFilter && <FilterDropdownItem />}
                            {!isStickyColumn(header.column) && (
                                <DropdownMenu.Item
                                    className={'flex items-center gap-1.5'}
                                    onClick={() => {
                                        header.column.pin('left');
                                    }}
                                >
                                    <SquaredIcon icon={faSnowflake} />
                                    Freeze
                                </DropdownMenu.Item>
                            )}
                            {isStickyColumn(header.column) && (
                                <DropdownMenu.Item
                                    className={'flex items-center gap-1.5'}
                                    onClick={() => header.column.pin(false)}
                                >
                                    <SquaredIcon icon={faSnowflakeDroplets} />
                                    Unfreeze
                                </DropdownMenu.Item>
                            )}
                            {canVisualize && (
                                <DropdownMenu.Item
                                    className={'flex items-center gap-1.5'}
                                    onClick={() =>
                                        table.options.meta?.onVisualizeColumn?.(header.column)
                                    }
                                >
                                    <SquaredIcon icon={faChartLineUp} />
                                    Visualize
                                </DropdownMenu.Item>
                            )}
                            {canHide && (
                                <DropdownMenu.Item
                                    className={'flex items-center gap-1.5'}
                                    onClick={() => {
                                        header.column.toggleVisibility(false);
                                        table.options.meta?.onHideColumn?.(header.column, table);
                                    }}
                                >
                                    <SquaredIcon icon={faEyeSlash} />
                                    Hide column
                                </DropdownMenu.Item>
                            )}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                    {!isActionColumn && canOrder && (
                        <Button
                            key={header.column.id}
                            {...listeners}
                            {...attributes}
                            variant="transparent"
                            className={twMerge(
                                'invisible my-auto h-3 w-3 cursor-grab p-0 text-gray-500 group-hover/th:visible',
                                className,
                            )}
                        >
                            <FontAwesomeIcon
                                className={'h-full w-full'}
                                icon={faGripDotsVertical}
                            />
                        </Button>
                    )}
                </div>
            )}
        </TableHeaderData>
    );
};

const FilterDropdownItem = () => {
    const updateFilters = useFiltersCache(state => state.updateFiltersNextByKey);
    const { table } = useContext(TableContext);
    const { header } = useContext(TableHeaderContext);
    const filters = table.getState().givebutterColumnFilters[header.id];

    return (
        <DropdownMenu.Item
            className={'flex items-center gap-1.5'}
            onClick={() => {
                updateFilters('tableFilters', filters ?? defaultFilterGroups);
                table.updateCurrentlyEditingColumn(header.column);
            }}
        >
            <SquaredIcon icon={faBarsFilter} />
            Edit filters
        </DropdownMenu.Item>
    );
};

const SquaredIcon = (props: FontAwesomeIconProps) => (
    <div className={'flex size-4 items-center justify-center [&>svg]:h-full [&>svg]:w-full'}>
        <FontAwesomeIcon {...props} />
    </div>
);

export { TableHeaderDataActionable };
export type { TableHeaderDataActionableProps };
